/**
 * Resolve the best target URL for GenPT payloads (preserve http/https and port, e.g. http://10.0.12.249:3000).
 *
 * ROAR-4446: When the user (or `latest_session`) provides a full URL with an explicit `http://` or `https://`
 * scheme, that value wins over `validated_target_url` from reachability/validation. Otherwise the backend
 * canonical URL is preferred (redirect / normalization). Scheme-less host strings still fall through to
 * validated and other sources as before.
 */

function trimStr(v) {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

function hasExplicitUrlScheme(url) {
  return /^https?:\/\//i.test(trimStr(url));
}

/**
 * Walk messages newest-first; return first validation_response.validated_target_url.
 */
export function getLatestValidatedTargetUrlFromMessages(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return '';
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const v = trimStr(messages[i]?.metadata?.validation_response?.validated_target_url);
    if (v) return v;
    const v2 = trimStr(messages[i]?.metadata?.validated_target_url);
    if (v2) return v2;
  }
  return '';
}

/**
 * @param {object} options
 * @param {string|null} options.targetURLProp - Chat panel state / query param
 * @param {object|null} options.msg - Current widget message (optional)
 * @param {object|null} options.latestSessionOverride - latest_session if already extracted
 * @param {object|null} options.sessionDetails - Optional session payload from parent
 * @param {object[]|null} options.messages - Full chat list (optional, for validated URL scan)
 */
export function getCanonicalGenptTargetUrl({
  targetURLProp = null,
  msg = null,
  latestSessionOverride = null,
  sessionDetails = null,
  messages = null,
} = {}) {
  const ls = latestSessionOverride ?? msg?.metadata?.latest_session ?? null;

  const validatedFromMessages = getLatestValidatedTargetUrlFromMessages(messages);

  const fromProp = trimStr(targetURLProp);
  const fromLs = trimStr(ls?.target_url);
  const explicitUserOrSession =
    (hasExplicitUrlScheme(fromProp) && fromProp) || (hasExplicitUrlScheme(fromLs) && fromLs) || '';

  const picked =
    explicitUserOrSession ||
    trimStr(sessionDetails?.validated_target_url) ||
    trimStr(msg?.metadata?.validation_response?.validated_target_url) ||
    trimStr(msg?.metadata?.validated_target_url) ||
    validatedFromMessages ||
    fromProp ||
    fromLs ||
    trimStr(msg?.metadata?.target_url) ||
    trimStr(msg?.metadata?.targetURL) ||
    trimStr(msg?.metadata?.asset?.url) ||
    '';

  return picked || null;
}
